import { useRouter } from "@tanstack/react-router";
import type { Me } from "@unbox/api";
import { createContext, type FC, type PropsWithChildren, useCallback, useContext, useEffect } from "react";
import { useMe } from "@/api/me";
import { queryClient } from "@/providers/QueryClientProviderWrapper";

type ContextProps = {
  isAuthenticated: boolean | undefined;
  user: Me | undefined;
  onSignOut: () => Promise<void>;
  onSignIn: () => Promise<void>;
};
export const AuthContext = createContext<ContextProps>({
  isAuthenticated: undefined,
  user: undefined,
  onSignOut: async () => {},
  onSignIn: async () => {},
});

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isLoading, refetch, data } = useMe({ retry: 0 });
  const isAuthenticated = isLoading ? undefined : !!data;

  const onSignOut = useCallback(async () => {
    queryClient.clear();
    await refetch();
  }, [refetch]);

  const onSignIn = useCallback(async () => {
    queryClient.clear();
    await refetch();
  }, [refetch]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user: data ?? undefined, onSignOut, onSignIn }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const auth = useContext(AuthContext);
  const router = useRouter();
  if (!auth) throw new Error("AuthContext not defined");
  // If you are calling useAuth above the RouterProvider definition, use useContext(AuthContext instead)
  if (!router?.invalidate) throw new Error("RouterContext not defined");

  useEffect(() => {
    router.invalidate();
  }, [auth.isAuthenticated, router.invalidate]);
  return auth;
};
